import { GameRulesConfig, GameRulesScoringItem, GameRulesSlide, HitsterEvent } from '../types';

const DEFAULT_SCORING_ITEMS: GameRulesScoringItem[] = [
  { points: '1 pt', label: 'Titel correct' },
  { points: '1 pt', label: 'Artiest correct' },
  { points: '1 pt', label: 'Jaar correct' },
  { points: '1 pt', label: 'Positie correct (links/rechts)' },
  { points: '4 pt', label: 'Maximum per nummer' }
];

const DEFAULT_GAME_RULES_SLIDES: GameRulesSlide[] = [
  {
    id: 'title',
    type: 'title',
    icon: '🎵',
    title: '{eventName}',
    subtitle: 'Muziekquiz',
    hintText: 'Druk op → of spatie om verder te gaan'
  },
  {
    id: 'flyer',
    type: 'flyer',
    title: 'Introductieflyer'
  },
  {
    id: 'goal',
    type: 'text',
    icon: '🎯',
    title: 'Doel van het spel',
    lines: [
      'Luister naar fragmenten van bekende nummers en raad de titel, artiest en het jaar van uitkomst.',
      'Plaats het nummer vervolgens op de juiste plek in je tijdlijn!'
    ]
  },
  {
    id: 'howto',
    type: 'steps',
    icon: '📝',
    title: 'Hoe werkt een ronde?',
    lines: [
      'Elk team krijgt een startkaart met een jaartal en 9 lege kaartjes',
      'Er worden 10 nummers afgespeeld (~30 sec per nummer)',
      'Schrijf per nummer de titel, artiest en jaartal op',
      'Leg het kaartje links of rechts van je startjaar'
    ]
  },
  {
    id: 'scoring',
    type: 'scoring',
    icon: '⭐',
    title: 'Punten verdienen',
    items: DEFAULT_SCORING_ITEMS
  },
  {
    id: 'position',
    type: 'position',
    icon: '📍',
    title: 'Positie scoren',
    leftLabel: '◀ LINKS',
    leftDescription: 'Ouder dan startjaar',
    centerLabel: 'START',
    rightLabel: 'RECHTS ▶',
    rightDescription: 'Nieuwer dan startjaar',
    footer: 'Ligt je kaartje aan de juiste kant? +1 punt!'
  },
  {
    id: 'teams',
    type: 'teams',
    icon: '🏆',
    title: 'Teams vanavond',
    emptyMessage: 'De teams worden later bekendgemaakt'
  },
  {
    id: 'goodluck',
    type: 'final',
    icon: '🎵',
    title: '{roundCount} Rondes × 10 Nummers',
    summary: '{songCount} nummers totaal',
    message: 'Veel plezier en succes!'
  }
];

const cloneScoringItems = (items?: GameRulesScoringItem[]) => (items || []).map(item => ({ ...item }));

const cloneSlides = (slides?: GameRulesSlide[]) => {
  return (slides || []).map(slide => ({
    ...slide,
    lines: slide.lines ? [...slide.lines] : undefined,
    items: slide.items ? cloneScoringItems(slide.items) : undefined
  }));
};

const buildSlidesFromLegacyConfig = (config?: GameRulesConfig | null): GameRulesSlide[] => {
  const slides = cloneSlides(DEFAULT_GAME_RULES_SLIDES);

  const getSlide = (id: string) => slides.find(slide => slide.id === id);

  const titleSlide = getSlide('title');
  if (titleSlide) {
    titleSlide.subtitle = config?.subtitle ?? titleSlide.subtitle;
    titleSlide.hintText = config?.hintText ?? titleSlide.hintText;
  }

  const goalSlide = getSlide('goal');
  if (goalSlide) {
    goalSlide.title = config?.goalTitle ?? goalSlide.title;
    goalSlide.lines = Array.isArray(config?.goalParagraphs) ? [...(config?.goalParagraphs || [])] : goalSlide.lines;
  }

  const roundSlide = getSlide('howto');
  if (roundSlide) {
    roundSlide.title = config?.roundTitle ?? roundSlide.title;
    roundSlide.lines = Array.isArray(config?.roundSteps) ? [...(config?.roundSteps || [])] : roundSlide.lines;
  }

  const scoringSlide = getSlide('scoring');
  if (scoringSlide) {
    scoringSlide.title = config?.scoringTitle ?? scoringSlide.title;
    scoringSlide.items = Array.isArray(config?.scoringItems)
      ? cloneScoringItems(config?.scoringItems || [])
      : scoringSlide.items;
  }

  const positionSlide = getSlide('position');
  if (positionSlide) {
    positionSlide.title = config?.positionTitle ?? positionSlide.title;
    positionSlide.leftLabel = config?.positionLeftLabel ?? positionSlide.leftLabel;
    positionSlide.leftDescription = config?.positionLeftDescription ?? positionSlide.leftDescription;
    positionSlide.centerLabel = config?.positionCenterLabel ?? positionSlide.centerLabel;
    positionSlide.rightLabel = config?.positionRightLabel ?? positionSlide.rightLabel;
    positionSlide.rightDescription = config?.positionRightDescription ?? positionSlide.rightDescription;
    positionSlide.footer = config?.positionFooter ?? positionSlide.footer;
  }

  const teamsSlide = getSlide('teams');
  if (teamsSlide) {
    teamsSlide.title = config?.teamsTitle ?? teamsSlide.title;
    teamsSlide.emptyMessage = config?.teamsEmptyMessage ?? teamsSlide.emptyMessage;
  }

  const finalSlide = getSlide('goodluck');
  if (finalSlide) {
    finalSlide.title = config?.finalTitle ?? finalSlide.title;
    finalSlide.summary = config?.finalSummary ?? finalSlide.summary;
    finalSlide.message = config?.finalMessage ?? finalSlide.message;
  }

  return slides;
};

export const getDefaultGameRulesConfig = (): GameRulesConfig => ({
  slides: cloneSlides(DEFAULT_GAME_RULES_SLIDES)
});

export const normalizeGameRulesConfig = (config?: GameRulesConfig | null): GameRulesConfig => {
  const normalizedSlides = Array.isArray(config?.slides) && (config?.slides?.length || 0) > 0
    ? cloneSlides(config?.slides)
    : buildSlidesFromLegacyConfig(config);

  return {
    ...config,
    slides: normalizedSlides
  };
};

export const renderGameRulesTemplate = (text: string | undefined, event: HitsterEvent): string => {
  if (!text) return '';

  const songCount = event.rounds.reduce((total, round) => total + round.songs.length, 0);
  const replacements: Record<string, string> = {
    eventName: event.name,
    roundCount: String(event.rounds.length),
    songCount: String(songCount),
    teamCount: String((event.teams || []).length)
  };

  return text.replace(/\{(eventName|roundCount|songCount|teamCount)\}/g, (_, key: string) => {
    return replacements[key] ?? '';
  });
};